import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Snackbar,
    Alert,
} from "@mui/material";
import { useState } from "react";
import dayjs from "dayjs";
import { MatchDialog } from "./MatchDialog.tsx";
import { matchService } from "../api/service.ts";
import { MatchDTO } from "./MatchResult/matchTypes.tsx";
import { useAuth } from "../AuthContext.tsx";

type MatchHistoryDialogProps = {
    open: boolean;
    onClose: () => void;
};

export default function MatchHistoryDialog({ open, onClose }: MatchHistoryDialogProps) {
    const { isAdmin } = useAuth();
    const [startDate, setStartDate] = useState(dayjs().subtract(1, "year").format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
    const [matches, setMatches] = useState<MatchDTO[]>([]);
    const [selected, setSelected] = useState<MatchDTO | null>(null);

    const [confirmDelete, setConfirmDelete] = useState<MatchDTO | null>(null);
    const [deleting, setDeleting] = useState(false);

    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({ open: false, message: "", severity: "success" });

    const handleSearch = async () => {
        const data = await matchService.getMatchesBetween(startDate, endDate);
        setMatches(data);
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            setDeleting(true);
            await matchService.deleteMatch(confirmDelete.id);
            setMatches((prev) => prev.filter((m) => m.id !== confirmDelete.id));
            setConfirmDelete(null);
            setSnackbar({
                open: true,
                message: "Match eliminato con successo.",
                severity: "success",
            });
        } catch (err) {
            console.error("Errore durante l'eliminazione:", err);
            setSnackbar({
                open: true,
                message: "Errore durante l'eliminazione del match.",
                severity: "error",
            });
        } finally {
            setDeleting(false);
        }
    };

    const renderResult = (m: MatchDTO) => {
        return `${m.finalResult}`;
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ backgroundColor: "primary.main" }}>
                    Storico Incontri
                </DialogTitle>

                <DialogContent>
                    <Stack direction="row" spacing={1} mb={1} mt={2}>
                        <TextField
                            label="Dal"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Al"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            fullWidth
                        />
                        <Button variant="contained" onClick={handleSearch}>
                            Cerca
                        </Button>
                    </Stack>

                    {matches.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Nessun match trovato
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Data</TableCell>
                                    <TableCell>Avversario</TableCell>
                                    <TableCell>Risultato</TableCell>
                                    <TableCell>Azioni</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {matches.map((m) => (
                                    <TableRow key={m.id}>
                                        <TableCell>{m.date}</TableCell>
                                        <TableCell>{m.opponent}</TableCell>
                                        <TableCell>{renderResult(m)}</TableCell>

                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <Button size="small" onClick={() => setSelected(m)}>
                                                    Dettagli
                                                </Button>
                                                {isAdmin && (
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        onClick={() => setConfirmDelete(m)}
                                                    >
                                                        Elimina
                                                    </Button>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose}>Chiudi</Button>
                </DialogActions>
            </Dialog>

            {selected && (
                <MatchDialog
                    matchId={selected.id}
                    eventId={selected.eventId}
                    onClose={() => setSelected(null)}
                    open={selected != null}
                    onReset={isAdmin ? () => {
                        setMatches(prev => prev.filter(m => m.id !== selected.id));
                        setSelected(null);
                    } : undefined}
                />
            )}

            <Dialog
                open={!!confirmDelete}
                onClose={() => !deleting && setConfirmDelete(null)}
            >
                <DialogTitle>Conferma eliminazione</DialogTitle>
                <DialogContent>
                    <Typography>
                        Sei sicuro di voler eliminare il match del{" "}
                        <strong>{confirmDelete?.date}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDelete(null)}
                        disabled={deleting}
                    >
                        Annulla
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleting}
                    >
                        {deleting ? "Eliminando..." : "Elimina"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
